import React from 'react';
import { Key, Cpu, BarChart2, Trash2, Shield, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useData } from '../contexts/DataContext';
import { MODEL_OPTIONS } from '../lib/constants';

export default function SettingsPage() {
  const { history, clearHistory, model, saveModel } = useData();
  const historyCount = history.length;

  const handleClearHistory = () => {
    if (!window.confirm(`Rensa ${historyCount} genereringar från historiken?`)) return;
    clearHistory();
    toast.success('Historik rensad');
  };

  const selectedModel = MODEL_OPTIONS.find(m => m.value === model) || MODEL_OPTIONS[0];

  const handleModelChange = (val) => {
    saveModel(val);
    const m = MODEL_OPTIONS.find(o => o.value === val);
    toast.success(`Din standard-modell satt till ${m?.label}`);
  };

  return (
    <div className="page-scroll">
      <div className="page-header">
        <h1 className="page-title">Inställningar</h1>
        <p className="page-subtitle">Konfiguration och verktygsinfo</p>
      </div>

      <div className="settings-list">
        <div className="settings-card">
          <div className="settings-row">
            <div className="settings-row-icon settings-row-icon--blue">
              <Key size={16} />
            </div>
            <div className="settings-row-body">
              <p className="settings-row-label">API-nycklar</p>
              <p className="settings-row-value">
                <span className="settings-status settings-status--ok">
                  <Shield size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Säkrade på server — nycklar är aldrig exponerade i webbläsaren
                </span>
              </p>
              <p className="settings-row-desc">Gemini, Claude och GPT-4o konfigureras via backend/.env</p>
            </div>
          </div>

          <div className="settings-divider" />

          <div className="settings-row settings-row--col">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div className="settings-row-icon settings-row-icon--cyan">
                <Cpu size={16} />
              </div>
              <div className="settings-row-body">
                <p className="settings-row-label">Min standard-AI-modell</p>
                <p className="settings-row-desc">Din personliga standard-modell. Lagras per användare och kan överskridas per körning (t.ex. i Nyhetsbrev).</p>
              </div>
            </div>
            <div className="model-selector-wrap" data-testid="model-selector">
              {MODEL_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`model-option-${opt.value}`}
                  className={`model-option${model === opt.value ? ' model-option--active' : ''}`}
                  onClick={() => handleModelChange(opt.value)}
                  style={{ '--model-color': opt.color }}
                >
                  <span className="model-dot" />
                  <div className="model-option-text">
                    <span className="model-option-label">{opt.label}</span>
                    <span className="model-option-sub">{opt.sub}</span>
                  </div>
                  {model === opt.value && <span className="model-check">✓</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="settings-divider" />

          <div className="settings-row">
            <div className="settings-row-icon settings-row-icon--emerald">
              <BarChart2 size={16} />
            </div>
            <div className="settings-row-body">
              <p className="settings-row-label">Ordmål</p>
              <p className="settings-row-value">150–220 ord per beskrivning</p>
            </div>
          </div>
        </div>

        <div className="settings-card">
          <div className="settings-row">
            <div className="settings-row-icon settings-row-icon--red">
              <Trash2 size={16} />
            </div>
            <div className="settings-row-body">
              <p className="settings-row-label">Rensa historik</p>
              <p className="settings-row-desc">{historyCount} genereringar sparade i Supabase</p>
            </div>
            <button
              data-testid="clear-history-btn"
              className="settings-action-btn settings-action-btn--danger"
              onClick={handleClearHistory}
              disabled={historyCount === 0}
            >
              Rensa historik
            </button>
          </div>
        </div>

        <div className="settings-card settings-card--info">
          <p className="settings-info-title">Om plattformen</p>
          <p className="settings-info-body">
            Vårdväskan AI Studio Beta v1.0. All data (mallar, vokabulär, loggar, historik) synkroniseras via Supabase och följer dig mellan enheter.
          </p>
        </div>
      </div>
    </div>
  );
}
